import { doc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { calculateWht, calculateGstWithheld, calculateExpectedReceipt, calculateRecoveryVariance } from "./whtCalculator";
import { normalizeCortevaInvoice } from "./adapters/cortevaAdapter";
import { normalizeIilInvoice } from "./adapters/iilAdapter";
import { normalizeCbreInvoice } from "./adapters/cbreAdapter";
import { normalizeGeneralEngineInvoice } from "./adapters/generalEngineAdapter";
import { normalizeUdInvoice } from "./adapters/udAdapter";
import { normalizeGSolarInvoice } from "./adapters/gsolarAdapter";
import { normalizeCortevaCropConsultantInvoice } from "./adapters/cortevaCropAdapter";

const GENERAL_ENGINE_CLIENTS = ["ARSL", "ATL", "ATL_COCACOLA", "AESL", "ES", "PTC_AKF", "SEFAM"];

// Single normalization entry point every approval function calls, so a
// client's invoice shape only has to be mapped to the 16-category enum once.
export function normalizeInvoice(clientCode, sourceDoc) {
  if (clientCode === "CORTEVA") return normalizeCortevaInvoice(sourceDoc);
  // IFS FO shares Corteva's own reimbursement + service-charge + GST shape,
  // just billed under Corteva Crop's NTN — only the flat-fee Consultant
  // invoice (no reimbursement, its own two-rate tax structure) needs the
  // dedicated adapter.
  if (clientCode === "CORTEVA_CROP") {
    return sourceDoc.segmentKey === "consultantCrop"
      ? normalizeCortevaCropConsultantInvoice(sourceDoc)
      : normalizeCortevaInvoice(sourceDoc);
  }
  if (clientCode === "IIL") return normalizeIilInvoice(sourceDoc);
  if (clientCode === "CBRE") return normalizeCbreInvoice(sourceDoc);
  if (clientCode === "UNITED_DETERGENT") return normalizeUdInvoice(sourceDoc);
  if (clientCode === "G_SOLAR" || clientCode === "VS_ALPHA") return normalizeGSolarInvoice(sourceDoc);
  if (GENERAL_ENGINE_CLIENTS.includes(clientCode)) return normalizeGeneralEngineInvoice(sourceDoc);
  throw new Error(`No invoice normalization adapter for client ${clientCode}.`);
}

// Builds the fields for one approved_invoice_snapshots doc. Pure/side-effect
// free except for reading auth.currentUser and serverTimestamp() — callers
// write the returned object inside their own Firestore transaction alongside
// their existing per-client registry write, so both land atomically.
export function buildSnapshotFields({
  clientCode, sourceCollection, sourceDocId, invoiceNumber, monthKey,
  accountingPeriod, invoiceDate, taxProfile, sourceDoc
}) {
  // Without an active tax profile the WHT / GST-withheld would silently come out
  // as zero and the expected receipt would be wrong, so an invoice is not
  // approved until finance has configured the client.
  if (!taxProfile) {
    throw new Error(`${clientCode} has no active tax profile, so ${invoiceNumber || "this invoice"} cannot be approved. Open Client Tax Profiles, set up and activate the profile for ${clientCode} (WHT rate, GST rate, GST withholding, payment terms), then approve again.`);
  }
  const normalized = normalizeInvoice(clientCode, sourceDoc);
  const whtModel = taxProfile.whtModel || null;

  // WHT is worked out on the model the profile names (e.g. CBRE/CPBM: 7% of the
  // gross invoice) and the GST withheld on the invoice's own GST amount, so an
  // invoice carrying 18% rather than 16% GST still reconciles.
  const whtAmount = calculateWht(whtModel, {
    serviceCharges: normalized.serviceChargeSubtotal,
    gstAmount: normalized.gstAmount,
    grossAmount: normalized.grossAmount,
    whtRate: taxProfile.whtRate
  });
  const gstWithheldAmount = calculateGstWithheld({
    gstAmount: normalized.gstAmount,
    gstWithholdingRate: taxProfile.gstWithholdingRate
  });
  const expectedReceipt = calculateExpectedReceipt({
    grossAmount: normalized.grossAmount,
    whtAmount,
    gstWithheldAmount
  });

  let recoveryVariance = null;
  if (taxProfile?.recoveryMethod === "GROSS_UP_ADD_BACK" && normalized.recoveryAddBack != null) {
    recoveryVariance = calculateRecoveryVariance({ actualWhtDeducted: whtAmount, recoveryAddBack: normalized.recoveryAddBack });
  }

  return {
    sourceCollection,
    sourceDocId,
    clientCode,
    invoiceNumber,
    monthKey,
    accountingPeriod: accountingPeriod || monthKey,
    invoiceDate,
    taxProfileId: taxProfile?.id || null,
    taxProfileVersion: taxProfile?.version || null,
    // Frozen at approval: receivables aging must not move when a client's terms change later.
    paymentTermsDays: Number(taxProfile.paymentTermsDays ?? 30),
    ...(normalized.meta ? { invoiceMeta: normalized.meta } : {}),
    lines: normalized.lines,
    totals: {
      serviceChargeSubtotal: normalized.serviceChargeSubtotal,
      gstAmount: normalized.gstAmount,
      grossAmount: normalized.grossAmount,
      whtAmount,
      gstWithheldAmount,
      recoveryAddBack: normalized.recoveryAddBack,
      expectedReceipt
    },
    whtModelUsed: whtModel,
    recoveryVariance,
    recoveryVarianceOverridden: false,
    recoveryOverrideReason: null,
    recoveryOverrideBy: null,
    status: "APPROVED",
    approvedBy: auth.currentUser?.uid || null,
    approvedByEmail: auth.currentUser?.email || null,
    approvedAt: serverTimestamp()
  };
}

export function snapshotDocRef(sourceDocId) {
  return doc(db, "approved_invoice_snapshots", encodeURIComponent(sourceDocId));
}

// Returns whether a Model-2 recovery variance is within the tax profile's
// configured tolerance — approval UIs use this to block/require an override
// (requirement 4) rather than silently accepting any variance.
export function checkRecoveryVariance(variance, tolerance) {
  if (variance == null) return { withinTolerance: true, variance: null };
  return { withinTolerance: Math.abs(variance) <= Number(tolerance || 0), variance };
}
